import * as Sentry from '@sentry/react'
import {SLOW_THRESHOLD} from '../types/perf'
import {tickOperation} from './session-context'

/** @param {string} name @param {number} duration @param {Record<string, unknown>} [ctx] */
export const checkSlow = (name, duration, ctx = {}) => {
  const key = Object.keys(SLOW_THRESHOLD).find((k) => name.includes(k))
  if (!key) return
  const threshold = SLOW_THRESHOLD[key]
  if (duration <= threshold) return
  Sentry.captureMessage(`[Perf] Slow: ${name} took ${duration.toFixed(0)}ms`, {
    level: duration > threshold * 3 ? 'error' : 'warning',
    extra: {duration_ms: duration, threshold_ms: threshold, ...ctx},
  })
}

const _applyContext = (tx, context = {}) => {
  const {tags = {}, measurements = {}} = context
  Object.entries(tags).forEach(([k, v]) => tx.setTag(k, String(v)))
  Object.entries(measurements).forEach(([k, v]) =>
    tx.setMeasurement(k, Number(v), k.endsWith('_kb') || k === 'total_megapixels' ? 'none' : 'millisecond')
  )
  return {tags, measurements}
}

/** Wrap any sync/async operation with a Sentry v7 transaction. */
export const measure = async (name, op, fn, context = {}) => {
  const tx = Sentry.startTransaction({name, op})
  const start = performance.now()
  try {
    const result = await fn()
    const duration = performance.now() - start
    const {tags, measurements} = _applyContext(tx, context)
    tx.setMeasurement('duration_ms', duration, 'millisecond')
    tx.setStatus('ok')
    checkSlow(name, duration, {...tags, ...measurements})
    return result
  } catch (err) {
    tx.setStatus('internal_error')
    Sentry.captureException(err)
    throw err
  } finally {
    tx.finish()
    tickOperation()
  }
}

/** Report worker Timing[] as a single Sentry transaction with child spans. */
export const reportWorkerTimings = (timings, context = {}) => {
  const {startTimestamp, endTimestamp} = context
  const tx = Sentry.startTransaction({
    name: 'worker.canvas_capture',
    op: 'worker',
    ...(startTimestamp ? {startTimestamp: startTimestamp / 1000} : {}),
  })
  _applyContext(tx, context)
  let total = 0
  let spanOffset = 0
  timings.forEach((t) => {
    const spanStart = startTimestamp ? (startTimestamp + spanOffset) / 1000 : undefined
    const spanEnd = startTimestamp ? (startTimestamp + spanOffset + t.duration) / 1000 : undefined
    const span = tx.startChild({op: t.op, description: t.name, data: t.data, startTimestamp: spanStart})
    span.setStatus('ok')
    span.finish(spanEnd)
    tx.setMeasurement(t.name.replace(/\./g, '_'), t.duration, 'millisecond')
    total += t.duration
    spanOffset += t.duration
    if (t.op === 'canvas.export') checkSlow('worker_encode', t.duration, {phase: t.name, ...t.data})
  })
  tx.setMeasurement('total_duration_ms', total, 'millisecond')
  tx.setStatus('ok')
  tx.finish(endTimestamp ? endTimestamp / 1000 : undefined)
}
